import { CSSProperties, useState } from "react";
import './Calendar.css'
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import { CalendarContentProps } from "./Calendar.types";
import dayjs from "dayjs";
import { ArrowBack, ArrowForward, ArrowLeftTwoTone, Refresh } from "@mui/icons-material";
import { months, weekdays } from "../_function/common";
import { ListScheduleCleaningProjection } from "../../../hooks/scheduled_jobs/index.type";

export const getCalendarColorCode = (job_type: string) => {
  switch (job_type) {
    case 'adhoc_job':
      return {
        backgroundColor: '#EA4C1D',
        color: '#FFFFFF'
      }

    case 'charging_job':
      return {
        backgroundColor: '#0AC97F',
        color: '#FFFFFF'
      }

    case 'pick_up_job':
      return {
        backgroundColor: '#9D56F7',
        color: '#FFFFFF'
      }

    case 'cleaning_job':
      return {
        backgroundColor: '#1565C0',
        color: '#FFFFFF'
      }


    case 'delivery_job':
    default:
      return {
        backgroundColor: '#08d489',
        color: '#FAFAFA'
      }
  }
}

function CalendarDays({
  day,
  onSelectDate,
  contents
}: {
  day: Date,
  onSelectDate?: (date: Date, content?: CalendarContentProps | ListScheduleCleaningProjection) => void,
  contents?: {
    delivery: CalendarContentProps[],
    cleaning: ListScheduleCleaningProjection[]
  };
}) {
  let firstDayOfMonth = new Date(day.getFullYear(), day.getMonth(), 1);
  let weekdayOfFirstDay = firstDayOfMonth.getDay();
  let currentDays = [];

  for (let dayNum = 0; dayNum < 42; dayNum++) {
    if (dayNum === 0 && weekdayOfFirstDay === 0) {
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() - 7);
    } else if (dayNum === 0) {
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() + (dayNum - weekdayOfFirstDay));
    } else {
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() + 1);
    }

    let calendarDay = {
      currentMonth: (firstDayOfMonth.getMonth() === day.getMonth()),
      date: (new Date(firstDayOfMonth)),
      month: firstDayOfMonth.getMonth(),
      number: firstDayOfMonth.getDate(),
      selected: (firstDayOfMonth.toDateString() === day.toDateString()),
      year: firstDayOfMonth.getFullYear()
    }

    currentDays.push(calendarDay);
  }

  const deliveryContents = contents?.delivery || []
  const cleaningContents = contents?.cleaning || []

  const isContentExist = (date: Date) => {
    if ((!deliveryContents && !cleaningContents) || (deliveryContents.length === 0 && cleaningContents.length === 0)) {
      return false
    }
    return true
  }

  const findContents = (date: Date) => {
    if (!isContentExist(date)) {
      return {
        delivery: [],
        cleaning: [],
        mixed: []
      }
    }
    const dateFormat = "DD/MM/YYYY"
    const deliveryResult = deliveryContents.filter((item) => dayjs.unix(item.timestamp).format(dateFormat) === dayjs(date).format(dateFormat)) || []

    const cleaningResult = cleaningContents.filter((item) => dayjs.unix(item.start_timestamp).format(dateFormat) === dayjs(date).format(dateFormat)) || []

    return {
      delivery: deliveryResult,
      cleaning: cleaningResult,
      mixed: [
        ...deliveryResult,
        ...cleaningResult
      ]
    }
  }

  const handleSelectContent = (date: Date, content?: CalendarContentProps | ListScheduleCleaningProjection) => {
    onSelectDate?.(date, content)
  }

  const getColorCode = (data: CalendarContentProps | ListScheduleCleaningProjection): CSSProperties => {
    return getCalendarColorCode((data as any).job_type)
  }

  return (
    <div className="table-content">
      {
        currentDays.map((day, idx) => {
          return (
            <div key={idx} className={"calendar-day" + (day.currentMonth ? " current" : "") + (day.selected ? " selected" : "")}
              onClick={() => onSelectDate?.(day.date, isContentExist(day.date) ? findContents(day.date).delivery[0] : undefined)}
            >
              <Typography
                variant={"body1"}
                sx={{
                  fontSize: dayjs().isSame(day.date, 'date') ? '18px' : '16px',
                  fontWeight: dayjs().isSame(day.date, 'date') ? 700 : 500,
                }}
              >
                {day.number}
              </Typography>
              {isContentExist(day.date) && (
                <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', gap: '2px', bottom: '8px', left: '8px', right: '8px' }}>
                  {findContents(day.date).mixed.map((item, index) => index < 2 && (
                    <div onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // if (item.) {

                      // }
                      handleSelectContent(day.date, item)
                    }} key={index} style={{
                      ...getColorCode(item),
                      userSelect: 'none',
                      borderRadius: '4px',
                      display: "flex",
                      flexDirection: "column",
                      cursor: 'pointer',
                    }}>
                      <div style={{ padding: '6px', display: 'flex', flexDirection: 'row' }}>
                        <span style={{
                          fontFamily: "Roboto, Hevetica, sans-serif",
                          fontSize: '12px',
                          marginRight: '4px'
                        }}>
                          {dayjs.unix('timestamp' in item ? item.timestamp : item.start_timestamp).format("HH:mm")}
                        </span>
                        <span style={{
                          flex: 'auto',
                          fontFamily: "Roboto, Hevetica, sans-serif",
                          fontSize: '12px',
                          display: "-webkit-box",
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          WebkitLineClamp: 1,
                        }}>
                          {item.title ?? item.id}
                        </span>
                        {
                          findContents(day.date).mixed.length > 2 && index === 1 && (
                            <span style={{
                              fontFamily: "Roboto, Hevetica, sans-serif",
                              fontSize: '12px',
                              fontWeight: 700
                            }}>
                              {findContents(day.date).mixed.length - 2}+
                            </span>
                          )
                        }
                      </div>
                    </div>
                  ))
                  }
                </div>
              )
              }
            </div>
          )
        })
      }
    </div>
  )
}
export default function Calendar({
  selectedDate,
  onSelectDate,
  dateContents,
  onRefetch
}: {
  selectedDate?: Date,
  onSelectDate?: (date: Date, content?: CalendarContentProps | ListScheduleCleaningProjection) => void;
  dateContents?: {
    delivery: CalendarContentProps[],
    cleaning: ListScheduleCleaningProjection[]
  },
  onRefetch?: () => void
}) {
  const [currentDate, setCurrentDate] = useState(selectedDate ?? new Date());

  const handleSelectDate = (date: Date, content?: CalendarContentProps | ListScheduleCleaningProjection) => {
    setCurrentDate(date)
    onSelectDate?.(date, content)
  }

  return (
    <div className="calendar" style={{
      color: '#000000',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        padding: '8px'
      }}>
        <div className="calendar-header">
          <Typography variant="body1" textAlign={'left'}>
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </Typography>
        </div>

        <div style={{
          display: "flex",
          flexDirection: 'row'
        }}>
          {!!onRefetch && (
            <Button
              sx={{
                color: '#23c667'
              }}
              onClick={() => onRefetch()}>
              <Refresh />
            </Button>
          )}
          <Button
            sx={{
              color: '#23c667'
            }}
            onClick={() => handleSelectDate(dayjs(selectedDate).subtract(1, 'month').toDate())}>
            <ArrowBack />
          </Button>
          <Button
            sx={{
              color: '#23c667'
            }}
            onClick={() => handleSelectDate(dayjs(selectedDate).add(1, 'month').toDate())}
          >
            <ArrowForward />
          </Button>
        </div>
      </div>
      <div className="calendar-body">
        <div className="table-header">
          {
            weekdays.map((weekday, index) => {
              return <div className="weekday" key={index}>
                <Typography variant="subtitle1">{weekday}</Typography>
              </div>
            })
          }
        </div>
        <div className="table">
          <CalendarDays
            day={currentDate}
            onSelectDate={(date, content) => handleSelectDate(date, content)}
            contents={dateContents}
          />
        </div>
      </div>
    </div>
  )
}